import React, { createContext, useState, useMemo, useContext, useRef, useCallback } from "react"

type onClick = () => void
type id = string

const useConstant = (fn: () => onClick) => {
  const ref: React.MutableRefObject<{ v: onClick } | undefined> = useRef()
  if (!ref.current) ref.current = { v: fn() }
  return ref.current.v
}

const TabsState = createContext<[id, React.Dispatch<React.SetStateAction<id>>]>(["", () => ""])

export const Tabs = ({ children, active }: { children: React.ReactNode; active: id }) => (
  <TabsState.Provider value={useState(() => active)}>
    <React.Suspense fallback={null}>{children}</React.Suspense>
  </TabsState.Provider>
)

interface ChildrenFunctionProps {
  (props: { onClick: onClick; isActive: boolean }): JSX.Element
}

export const Tab = ({ children, id }: { children: ChildrenFunctionProps | JSX.Element; id: id }): JSX.Element => {
  const [active, setActive] = useContext(TabsState)

  const onClick = useConstant(() => () => setActive(id))
  const state = useMemo(
    () => ({
      isActive: active === id,
      onClick
    }),
    [active, onClick, id]
  )

  if (typeof children === "function") return children(state)

  return React.isValidElement(children) ? React.cloneElement(children, state) : children
}

export const Panel = ({
  children,
  component,
  idFor
}: {
  children?: JSX.Element
  component?: JSX.Element
  idFor: id
}): JSX.Element | null => {
  const [active] = useContext(TabsState)
  const isActive = idFor === active
  if (children) return isActive ? children : null
  if (component) return isActive ? component : null
  return null
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTab = () => {
  const [, setActive] = useContext(TabsState)

  const onClick = useCallback(
    (id: id) => {
      setActive(id)
    },
    [setActive]
  )

  return { onClick }
}
